import React, { forwardRef, ReactNode, useMemo } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import { Typography } from '@mui/material';
import {
  FieldPathByValue,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { Collapse } from 'src/components/Collapse';
import { CustomFormulaValues } from 'src/components/Forms/CustomFormulaForm';
import { Rule } from 'src/components/Forms/CustomFormulaForm/Filters/FilterCollapse/Rule';
import { RulesGroup } from 'src/components/Forms/CustomFormulaForm/Filters/FilterCollapse/RulesGroup';
import {
  AddButton,
  ButtonsContainer,
  ConditionalDivider,
  InnerContainer,
  Wrapper,
} from 'src/components/Forms/CustomFormulaForm/Filters/FilterCollapse/styled';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { countFilledFilters } from 'src/utils';
import {
  GET_AVAILABLE_FILTERS_FOR_METRIC,
  GET_FILTERS,
  GET_DATABASE_SPLITS_FOR_CUSTOM_REPORT,
} from 'src/utils/gql/queries';
import {
  ConnectorType,
  MetricFilterItem,
  Query,
  QueryGetAvailableFiltersForMetricArgs,
  QueryGetAvailableFiltersArgs,
  Filter,
  FilterValueType,
} from 'src/utils/gql/types';

import { OperatorEnum } from '../../../../DashboardTable/types';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';
import { ensureFidodbGeoSplits } from 'src/components/DatabaseConnector/ensureFidodbGeoSplits';

export enum FilterOperatorLabel {
  Is = 'Is',
  IsNot = 'Is Not',
  Contains = 'Contains',
  NotContains = 'Does Not Contain',
}

export const defaultOperators = [
  { key: OperatorEnum.In, label: FilterOperatorLabel.Is },
  { key: OperatorEnum.NotIn, label: FilterOperatorLabel.IsNot },
  { key: OperatorEnum.Contain, label: FilterOperatorLabel.Contains },
  { key: OperatorEnum.NotContain, label: FilterOperatorLabel.NotContains },
];

interface FilterCollapseProps {
  label: string;
  icon: ReactNode;
  expanded: boolean;
  metricId: string;
  connectorType: ConnectorType;
  formulaItemIndex: number;
  onToggle: () => void;
}

export const FilterCollapse = forwardRef<HTMLDivElement, FilterCollapseProps>(
  (
    {
      metricId,
      connectorType,
      label,
      icon,
      expanded,
      formulaItemIndex,
      onToggle,
    },
    ref,
  ) => {
    const snackbar = useSnackbar();
    const {
      control,
      getValues,
      setValue,
      formState: { errors },
    } = useFormContext<CustomFormulaValues>();

    const error = errors.formulaItems?.[formulaItemIndex]?.filters;
    const filtersPath: `formulaItems.${number}.filters` = `formulaItems.${formulaItemIndex}.filters`;
    const count = countFilledFilters(getValues(filtersPath) || []);

    const { fields, append, remove } = useFieldArray({
      control,
      name: filtersPath,
    });

    const onError = (err: ApolloError) => {
      const { graphQLErrors, message: errorText } = err;
      const message = graphQLErrors.length
        ? graphQLErrors[0].message
        : errorText;

      snackbar(message);
    };

    // Per Facebook usa GET_FILTERS; per Database usa gli splits come dimensioni; per gli altri usa GET_AVAILABLE_FILTERS_FOR_METRIC
    const isFacebook = connectorType === ConnectorType.FACEBOOK;
    const isDatabase = connectorType === ConnectorType.DATABASE;
    const { databaseConnectorId, dbSplits } = useConnectors();

    const { data: filtersData, loading: gettingFilters } = useQuery<
      Pick<Query, 'getAvailableFiltersForMetric'>,
      QueryGetAvailableFiltersForMetricArgs
    >(GET_AVAILABLE_FILTERS_FOR_METRIC, {
      variables: { metricId },
      skip: !metricId || isFacebook || isDatabase,
      onError,
    });

    const { data: facebookFiltersData, loading: gettingFacebookFilters } =
      useQuery<Pick<Query, 'getFilters'>, QueryGetAvailableFiltersArgs>(
        GET_FILTERS,
        {
          variables: { connectorType: ConnectorType.FACEBOOK },
          skip: !isFacebook,
          onError,
        },
      );

    // Splits del Database da usare come dimensioni filtrabili
    const { data: dbSplitsData } = useQuery(
      GET_DATABASE_SPLITS_FOR_CUSTOM_REPORT,
      {
        variables: { connectorId: databaseConnectorId || '' },
        skip: !isDatabase || !databaseConnectorId,
        onError,
      },
    );

    // Combina i dati dei filtri
    const availableFilters: Filter[] = useMemo(() => {
      if (isFacebook) {
        return facebookFiltersData?.getFilters || [];
      }
      if (isDatabase) {
        const rawSplits =
          dbSplitsData?.getDatabaseConnectorSplitsForCustomReport ||
          dbSplits ||
          [];
        const normalizedSplits = rawSplits.map((s: any) => ({
          id: s.id || s.key,
          key: s.key,
          label: s.name || s.label || s.key,
          connectorType: ConnectorType.DATABASE,
          valueType: undefined,
        }));
        return ensureFidodbGeoSplits(normalizedSplits).map((s) => {
          const isGeoFilter =
            s.key === 'fidodb.provincia_cliente' ||
            s.key === 'fidodb.regione_cliente' ||
            s.key === 'fidodb.citta_cliente';
          return {
            id: s.id || s.key,
            type: ConnectorType.DATABASE,
            key: s.key,
            label: s.label || s.key,
            operators: defaultOperators,
            shouldAvailableValuesBeRequested: isGeoFilter,
            expectedReturnValueType: FilterValueType.ArrayStr,
          };
        });
      }
      return filtersData?.getAvailableFiltersForMetric || [];
    }, [
      isFacebook,
      isDatabase,
      metricId,
      facebookFiltersData?.getFilters,
      filtersData?.getAvailableFiltersForMetric,
      dbSplitsData?.getDatabaseConnectorSplitsForCustomReport,
      dbSplits,
    ]);

    const loading = isFacebook
      ? gettingFacebookFilters
      : isDatabase
        ? false
        : gettingFilters;

    const firstFilter = fields?.[0] as
      | undefined
      | { key?: string; filtersGroup?: Array<{ key?: string }> };

    const firstKey = useMemo(() => {
      return firstFilter?.key ?? firstFilter?.filtersGroup?.[0]?.key ?? '';
    }, [firstFilter]);

    const handleAddGroup = () =>
      append({
        filtersGroup: [
          { key: firstKey, operator: defaultOperators[0].key, values: [] },
        ],
      });
    const handleAddRule = () =>
      append({ key: firstKey, operator: defaultOperators[0].key, values: [] });

    const handleChangeDimension = (key: string) => {
      const matched = availableFilters.find((f) => f.key === key);
      const operator = matched?.operators?.[0]?.key || '';

      for (let i = 0; i < fields.length; i++) {
        setValue(`${filtersPath}.${i}.key`, key, { shouldValidate: true });
        setValue(`${filtersPath}.${i}.operator`, operator);
        setValue(`${filtersPath}.${i}.values`, []);
      }
    };

    return (
      <Collapse
        title={label}
        icon={icon}
        expanded={expanded}
        count={count}
        error={!!error?.length}
        ref={ref}
        onToggle={onToggle}
      >
        <Wrapper>
          {fields.map((filter, index) => (
            <InnerContainer key={filter.id}>
              {'filtersGroup' in filter ? (
                <RulesGroup
                  onChangeDimension={handleChangeDimension}
                  firstKey={firstKey}
                  keySelection={!index}
                  groupName={`Group of rules ${index + 1}`}
                  filters={availableFilters}
                  filterItemIndex={index}
                  formulaItemIndex={formulaItemIndex}
                  loading={loading}
                  onDeleteGroup={() => remove(index)}
                />
              ) : (
                <Rule
                  onChangeDimension={handleChangeDimension}
                  keySelection={!index}
                  ruleName={`Rule ${index + 1}`}
                  filterPath={
                    `${filtersPath}.${index}` as FieldPathByValue<
                      CustomFormulaValues,
                      MetricFilterItem
                    >
                  }
                  filters={availableFilters}
                  loading={loading}
                  onDelete={() => remove(index)}
                />
              )}
              {fields.length !== index + 1 && (
                <ConditionalDivider>OR</ConditionalDivider>
              )}
            </InnerContainer>
          ))}
          <ButtonsContainer>
            <AddButton onClick={handleAddRule}>
              <AddIcon />
              <Typography>Add rule</Typography>
            </AddButton>
            <AddButton onClick={handleAddGroup}>
              <AddIcon />
              <Typography>Add group</Typography>
            </AddButton>
          </ButtonsContainer>
        </Wrapper>
      </Collapse>
    );
  },
);
